// ---------------------------------------------------------------------------
// SpeedyDock SDK — Injected CSS for modal, drawer, and widgets
// ---------------------------------------------------------------------------

import type { ThemeConfig } from "./types";

export const SDK_PREFIX = "sd-sdk";

export function injectStyles(): void {
  if (document.getElementById(`${SDK_PREFIX}-styles`)) return;

  const style = document.createElement("style");
  style.id = `${SDK_PREFIX}-styles`;
  style.textContent = CSS;
  document.head.appendChild(style);
}

/**
 * Apply optional theme overrides from `SpeedyDock.init({ theme })`.
 *
 * Sets CSS custom properties on the document root. Any value left unspecified
 * keeps the default baked into the injected stylesheet, so the default look is
 * unchanged when no theme (or a partial theme) is provided.
 */
export function applyTheme(theme?: ThemeConfig): void {
  if (!theme || typeof document === "undefined") return;
  const root = document.documentElement;
  if (theme.primaryColor) {
    root.style.setProperty(`--${SDK_PREFIX}-primary`, theme.primaryColor);
    root.style.setProperty(`--${SDK_PREFIX}-primary-accent`, theme.primaryColor);
  }
  if (theme.borderRadius) {
    root.style.setProperty(`--${SDK_PREFIX}-fab-radius`, theme.borderRadius);
  }
}

const CSS = `
/* ── Theme tokens (overridable via SpeedyDock.init({ theme })) ── */
:root {
  --${SDK_PREFIX}-primary: #0369a1;
  --${SDK_PREFIX}-primary-accent: #0891b2;
  --${SDK_PREFIX}-fab-radius: 999px;
}
/* ── Shared ── */
.${SDK_PREFIX}-iframe {
  border: none;
  width: 100%;
  height: 100%;
  display: block;
  background: transparent;
  color-scheme: light;
}

/* ── Overlay backdrop (modal & drawer) ── */
.${SDK_PREFIX}-overlay {
  position: fixed;
  inset: 0;
  z-index: 999998;
  background: rgba(0, 0, 0, 0);
  transition: background 0.3s ease;
  display: none;
}
.${SDK_PREFIX}-overlay.open {
  display: block;
  background: rgba(0, 0, 0, 0.5);
}

/* ── Modal ── */
.${SDK_PREFIX}-modal {
  position: fixed;
  z-index: 999999;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0.95);
  opacity: 0;
  width: 96vw;
  max-width: 960px;
  height: 94vh;
  max-height: 94vh;
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 25px 60px -12px rgba(0,0,0,0.35);
  transition: transform 0.3s cubic-bezier(0.34,1.56,0.64,1), opacity 0.3s ease;
  display: none;
}
.${SDK_PREFIX}-modal.open {
  display: block;
  transform: translate(-50%, -50%) scale(1);
  opacity: 1;
}
.${SDK_PREFIX}-modal-close {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(0,0,0,0.06);
  color: #64748b;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.${SDK_PREFIX}-modal-close:hover {
  background: rgba(0,0,0,0.12);
  color: #0f172a;
}

/* ── Drawer ── */
.${SDK_PREFIX}-drawer {
  position: fixed;
  z-index: 999999;
  top: 0;
  bottom: 0;
  width: 540px;
  max-width: 100vw;
  background: #fff;
  box-shadow: -4px 0 30px rgba(0,0,0,0.15);
  transition: transform 0.35s cubic-bezier(0.33,1,0.68,1);
  display: none;
}
.${SDK_PREFIX}-drawer.right {
  right: 0;
  transform: translateX(100%);
}
.${SDK_PREFIX}-drawer.left {
  left: 0;
  transform: translateX(-100%);
}
.${SDK_PREFIX}-drawer.open {
  display: block;
  transform: translateX(0);
}
.${SDK_PREFIX}-drawer-close {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(0,0,0,0.06);
  color: #64748b;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.${SDK_PREFIX}-drawer-close:hover {
  background: rgba(0,0,0,0.12);
  color: #0f172a;
}

/* ── Inline container ── */
.${SDK_PREFIX}-inline {
  width: 100%;
  min-height: 600px;
  position: relative;
}

/* ── Floating "Book Now" button (auto-init) ── */
.${SDK_PREFIX}-fab {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 999990;
  padding: 14px 28px;
  border: none;
  border-radius: var(--${SDK_PREFIX}-fab-radius);
  background: linear-gradient(135deg, var(--${SDK_PREFIX}-primary), var(--${SDK_PREFIX}-primary-accent));
  color: #fff;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  box-shadow: 0 8px 24px -4px rgba(3,105,161,0.4), 0 2px 8px rgba(0,0,0,0.08);
  transition: transform 0.2s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.2s;
}
.${SDK_PREFIX}-fab:hover {
  transform: scale(1.05);
  box-shadow: 0 12px 32px -4px rgba(3,105,161,0.5), 0 4px 12px rgba(0,0,0,0.1);
}
.${SDK_PREFIX}-fab:active {
  transform: scale(0.97);
}

/* ── Search widget ── */
.${SDK_PREFIX}-search {
  display: flex;
  gap: 8px;
  align-items: center;
  font-family: system-ui, -apple-system, sans-serif;
}
.${SDK_PREFIX}-search input[type="date"] {
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  color: #0f172a;
  background: #fff;
  outline: none;
  transition: border-color 0.15s;
}
.${SDK_PREFIX}-search input[type="date"]:focus {
  border-color: var(--${SDK_PREFIX}-primary-accent);
  box-shadow: 0 0 0 3px rgba(8,145,178,0.15);
}
.${SDK_PREFIX}-search button {
  padding: 10px 20px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--${SDK_PREFIX}-primary), var(--${SDK_PREFIX}-primary-accent));
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
  white-space: nowrap;
}
.${SDK_PREFIX}-search button:hover {
  opacity: 0.9;
}

/* ── Boat card widget ── */
.${SDK_PREFIX}-boat-card {
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  background: #fff;
  box-shadow: 0 4px 16px -4px rgba(0,0,0,0.08);
  font-family: system-ui, -apple-system, sans-serif;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  max-width: 320px;
}
.${SDK_PREFIX}-boat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px -8px rgba(0,0,0,0.15);
}
.${SDK_PREFIX}-boat-card-img {
  width: 100%;
  height: 180px;
  object-fit: cover;
  display: block;
  background: linear-gradient(135deg, var(--${SDK_PREFIX}-primary), var(--${SDK_PREFIX}-primary-accent));
}
.${SDK_PREFIX}-boat-card-body {
  padding: 16px;
}
.${SDK_PREFIX}-boat-card-name {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px;
}
.${SDK_PREFIX}-boat-card-meta {
  font-size: 13px;
  color: #64748b;
  margin: 0 0 12px;
}
.${SDK_PREFIX}-boat-card-price {
  font-size: 14px;
  font-weight: 600;
  color: var(--${SDK_PREFIX}-primary);
  margin: 0 0 12px;
}
.${SDK_PREFIX}-boat-card-btn {
  display: block;
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--${SDK_PREFIX}-primary), var(--${SDK_PREFIX}-primary-accent));
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-align: center;
  transition: opacity 0.15s;
}
.${SDK_PREFIX}-boat-card-btn:hover {
  opacity: 0.9;
}

/* ── Loading spinner ── */
.${SDK_PREFIX}-spinner {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  z-index: 1;
  transition: opacity 0.3s;
}
.${SDK_PREFIX}-spinner.hidden {
  opacity: 0;
  pointer-events: none;
}
.${SDK_PREFIX}-spinner::after {
  content: "";
  width: 32px;
  height: 32px;
  border: 3px solid #e2e8f0;
  border-top-color: var(--${SDK_PREFIX}-primary-accent);
  border-radius: 50%;
  animation: ${SDK_PREFIX}-spin 0.7s linear infinite;
}
@keyframes ${SDK_PREFIX}-spin {
  to { transform: rotate(360deg); }
}

/* ── Reduced motion ── */
@media (prefers-reduced-motion: reduce) {
  .${SDK_PREFIX}-modal,
  .${SDK_PREFIX}-drawer,
  .${SDK_PREFIX}-overlay,
  .${SDK_PREFIX}-fab,
  .${SDK_PREFIX}-boat-card {
    transition: none !important;
  }
  .${SDK_PREFIX}-spinner::after {
    animation: none;
  }
}
`;
